import { Component, Input, OnInit } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { PageChangeEvent } from '@progress/kendo-angular-grid';
import { SortDescriptor } from '@progress/kendo-data-query';
import { IFilter, IGridResult, IGridState, PageSize } from 'src/app/core/models/base';
import { toDayBoundaryIso } from 'src/app/shared/helpers/date-helpers';
import { AppStateInterface } from 'src/app/stores/global/appState.interface';
import { currentEmployeeSelector } from 'src/app/stores/global/current-employee/current-employee.store';
import { PermissionStorageService } from 'src/app/authentication/storages/permission-storage.service';
import { ExpenseStoreService, IOwnExpensesFilter } from '../../storage/expense-store.service';
import { EXPENSES_APPROVE_SELF_PERMISSION, ExpenseStatus, IExpense, expenseSortToOrderBy } from '../../models/expense';

export type ExpenseListMode = 'own' | 'approval';

@UntilDestroy()
@Component({
    selector: 'app-expense-list',
    templateUrl: './expense-list.component.html',
    standalone: false
})
export class ExpenseListComponent implements OnInit {

  @Input() mode: ExpenseListMode = 'own';

  expenses: IGridResult<IExpense[]> = { data: [], total: 0 };
  selectedIds: string[] = [];

  // Server-side paging state (kept in sync with the store's request).
  gridState: IGridState = { pageSize: 25, skip: 0 };

  // Server-side sort. Approval mode starts by date so the review list can be worked through in the
  // same order as the accounting export. Own mode starts unsorted (set in ngOnInit, once the mode
  // input is known): without an orderBy the backend groups by status, and unsorting brings that back.
  sort: SortDescriptor[] = [{ field: 'date', dir: 'desc' }];

  get pageSizes() {
    return PageSize;
  }

  // --- filter (own list only): status + expense-date range ---
  filterForm = new UntypedFormGroup({
    status: new UntypedFormControl(null),        // null => all statuses
    dateRangeStart: new UntypedFormControl(null),
    dateRangeEnd: new UntypedFormControl(null)
  });

  // Selectable statuses for the filter dropdown; allStatusesItem is the "all" defaultItem.
  statusFilterItems = [
    { value: ExpenseStatus.Open },
    { value: ExpenseStatus.Submitted },
    { value: ExpenseStatus.Accepted },
    { value: ExpenseStatus.Rejected },
    { value: ExpenseStatus.Paid }
  ];
  allStatusesItem = { value: null as ExpenseStatus | null };

  // May the current user decide on their own expenses?
  hasApproveSelf = (this.permissionStorage.getPermissions() ?? [])
    .includes(EXPENSES_APPROVE_SELF_PERMISSION);

  private currentEmployeeId: string | null = null;

  // dialog state
  dialogOpen = false;
  dialogId: string | null = null;
  dialogEditable = false;
  rejectOpen = false;
  rejectId: string | null = null;

  // expose enum to template
  ExpenseStatus = ExpenseStatus;

  constructor(
    private expenseStore: ExpenseStoreService,
    private store: Store<AppStateInterface>,
    private permissionStorage: PermissionStorageService
  ) {
    this.store.select(currentEmployeeSelector)
      .pipe(untilDestroyed(this))
      .subscribe(employee => this.currentEmployeeId = employee?.id ?? null);
  }

  ngOnInit(): void {
    let stream$: Observable<IGridResult<IExpense[]>>;
    switch (this.mode) {
      case 'approval':
        stream$ = this.expenseStore.toReviewExpenses$;
        // Pass the default sort explicitly: the root store may still hold a previous sort.
        this.expenseStore.loadToReview(undefined, expenseSortToOrderBy(this.sort));
        break;
      default:
        stream$ = this.expenseStore.ownExpenses$;
        this.sort = [];
        // Pass the (empty) sort explicitly: the root store may still hold a previous sort.
        this.expenseStore.loadOwn(undefined, undefined, this.sort);
        break;
    }

    stream$.pipe(untilDestroyed(this)).subscribe(data => this.expenses = data);
  }

  // --- paging (server-side, mirrors the contact list) ---
  private reload(page: { pageNumber: number; pageSize: number }): void {
    if (this.mode === 'approval') {
      this.expenseStore.loadToReview(page);
    } else {
      this.expenseStore.loadOwn(page);
    }
  }

  onPageChange({ skip, take }: PageChangeEvent): void {
    this.gridState.skip = skip;
    this.gridState.pageSize = take;
    // Selection is per-page; drop it when the visible rows change.
    this.selectedIds = [];
    this.reload({ pageNumber: Math.round((skip + take) / take), pageSize: take });
  }

  onPageSizeChange(pageSize: number): void {
    this.onPageChange({ skip: this.gridState.skip, take: pageSize });
  }

  onGridReload(): void {
    this.reload({ pageNumber: Math.round((this.gridState.skip + this.gridState.pageSize) / this.gridState.pageSize), pageSize: this.gridState.pageSize });
  }

  // --- sorting (server-side) ---
  onSortChange(sort: SortDescriptor[]): void {
    this.sort = sort;
    this.gridState.skip = 0;
    this.selectedIds = [];
    const page = { pageNumber: 1, pageSize: this.gridState.pageSize };
    if (this.mode === 'approval') {
      this.expenseStore.loadToReview(page, expenseSortToOrderBy(sort));
    } else {
      this.expenseStore.loadOwn(page, undefined, sort);
    }
  }

  // --- filter (own list) ---
  onSubmitFilter(): void {
    this.selectedIds = [];
    this.gridState.skip = 0;
    this.expenseStore.loadOwn({ pageNumber: 1, pageSize: this.gridState.pageSize }, this.buildOwnFilter());
  }

  onResetFilter(): void {
    this.filterForm.reset();
    this.onSubmitFilter();
  }

  private buildOwnFilter(): IOwnExpensesFilter {
    const { status, dateRangeStart, dateRangeEnd } = this.filterForm.value;
    const statusValue: ExpenseStatus | null = status?.value ?? null;

    const dateFilters: IFilter[] = [];
    if (dateRangeStart) {
      dateFilters.push({ field: 'Date', operator: 'gte', value: toDayBoundaryIso(dateRangeStart, false) });
    }
    if (dateRangeEnd) {
      dateFilters.push({ field: 'Date', operator: 'lte', value: toDayBoundaryIso(dateRangeEnd, true) });
    }

    let advancedFilter: IFilter | undefined;
    if (dateFilters.length === 1) {
      advancedFilter = dateFilters[0];
    } else if (dateFilters.length > 1) {
      advancedFilter = { logic: 'and', filters: dateFilters };
    }

    return {
      statuses: statusValue == null ? [] : [statusValue],
      advancedFilter
    };
  }

  statusName(status: ExpenseStatus): string {
    return ExpenseStatus[status];
  }

  statusClass(status: ExpenseStatus): string {
    return 'status-' + ExpenseStatus[status].toLowerCase();
  }

  // --- own actions ---
  openCreate() {
    this.dialogId = null;
    this.dialogEditable = true;
    this.dialogOpen = true;
  }

  /** A row can be edited when it's an own Open expense, or an own Rejected one being reworked. */
  canEdit(expense: IExpense): boolean {
    return this.mode === 'own'
      && (expense.status === ExpenseStatus.Open || expense.status === ExpenseStatus.Rejected);
  }

  submitRow(expense: IExpense) {
    this.expenseStore.submitExpense(expense.id);
  }

  withdrawRow(expense: IExpense) {
    this.expenseStore.withdrawExpense(expense.id);
  }

  deletionDialogOpened = false;
  private expenseToDelete: IExpense | null = null;

  deleteRow(expense: IExpense) {
    this.expenseToDelete = expense;
    this.deletionDialogOpened = true;
  }

  deleteConfirmed() {
    const expense = this.expenseToDelete;
    this.expenseToDelete = null;
    if (!expense) return;
    this.expenseStore.deleteExpense(expense.id);
  }

  // --- view / edit ---
  open(expense: IExpense) {
    this.dialogId = expense.id;
    this.dialogEditable = this.canEdit(expense);
    this.dialogOpen = true;
  }

  closeDialog() {
    this.dialogOpen = false;
    this.dialogId = null;
    this.dialogEditable = false;
  }

  // --- approval actions ---

  /** Whether the current user may decide on this row (never their own, unless they hold ApproveSelf). */
  canDecide(expense: IExpense): boolean {
    return expense.submitterId !== this.currentEmployeeId || this.hasApproveSelf;
  }

  approveRow(expense: IExpense) {
    this.expenseStore.approveExpense(expense.id);
  }

  approveSelected() {
    // Bulk-approve only the rows the user is actually allowed to decide on.
    const ids = this.expenses.data
      .filter(e => this.selectedIds.includes(e.id) && this.canDecide(e))
      .map(e => e.id);
    this.expenseStore.approveMany(ids);
    this.selectedIds = [];
  }

  openReject(expense: IExpense) {
    this.rejectId = expense.id;
    this.rejectOpen = true;
  }

  confirmReject(reason: string) {
    if (this.rejectId) {
      this.expenseStore.rejectExpense({ id: this.rejectId, rejectionReason: reason });
    }
    this.closeReject();
  }

  closeReject() {
    this.rejectOpen = false;
    this.rejectId = null;
  }
}
